/**
 * Order InstantSearch multi-index sections by relevance (not a fixed type list).
 * Mirrors tradewing-v2-ms-opensearch cross-index reorder: text match first, then score.
 */

type HighlightNode = {
  matchLevel?: string;
  matchedWords?: string[];
  [key: string]: unknown;
};

type SearchHit = {
  objectID?: string;
  _relevanceScore?: number;
  _score?: number;
  _highlightResult?: HighlightNode;
  _snippetResult?: HighlightNode;
};

export type IndexHitsBlock = {
  index: string;
  hits: SearchHit[];
};

function hasTextMatch(node: unknown): boolean {
  if (!node || typeof node !== 'object') {
    return false;
  }
  if (Array.isArray(node)) {
    return node.some(hasTextMatch);
  }
  const record = node as HighlightNode;
  const level = typeof record.matchLevel === 'string' ? record.matchLevel : '';
  if (level.trim().toLowerCase() !== '' && level.trim().toLowerCase() !== 'none') {
    return true;
  }
  return Object.values(record).some(hasTextMatch);
}

function hitRelevance(hit: SearchHit): number {
  const raw = hit._relevanceScore ?? hit._score ?? 0;
  const score = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(score) ? score : 0;
}

/** Lower is better: 0 = title/name match, 1 = any text match, 2 = score-only, 3 = empty. */
function hitMatchPriority(hit: SearchHit): number {
  const highlight = hit._highlightResult || {};
  const snippet = hit._snippetResult || {};

  const titleish =
    hasTextMatch(highlight.title) ||
    hasTextMatch(highlight.name) ||
    hasTextMatch(highlight.displayName) ||
    hasTextMatch((highlight.file as HighlightNode | undefined)?.name) ||
    hasTextMatch(highlight['file.name']);

  if (titleish) {
    return 0;
  }
  if (hasTextMatch(highlight) || hasTextMatch(snippet)) {
    return 1;
  }
  return 2;
}

function blockMatchPriority(block: IndexHitsBlock): number {
  if (!block.hits?.length) {
    return 3;
  }
  return Math.min(...block.hits.map(hitMatchPriority));
}

function blockTopScore(block: IndexHitsBlock): number {
  if (!block.hits?.length) {
    return 0;
  }
  return Math.max(...block.hits.map(hitRelevance));
}

/**
 * Sort multi-index autocomplete sections so the strongest match group is first.
 * Optional `fallbackPriority` breaks ties using the old fixed type order.
 */
export function sortGlobalSearchSectionsByRelevance<T extends IndexHitsBlock>(
  sections: T[],
  fallbackPriority: Record<string, number> = {}
): T[] {
  return [...sections].sort((a, b) => {
    const matchDelta = blockMatchPriority(a) - blockMatchPriority(b);
    if (matchDelta !== 0) {
      return matchDelta;
    }
    const scoreDelta = blockTopScore(b) - blockTopScore(a);
    if (scoreDelta !== 0) {
      return scoreDelta;
    }
    const aFallback = fallbackPriority[a.index] ?? Number.MAX_SAFE_INTEGER;
    const bFallback = fallbackPriority[b.index] ?? Number.MAX_SAFE_INTEGER;
    return aFallback - bFallback;
  });
}
